/*
	Component for conditionally rendering content depending on user permissions.

	You may provide `any` as an array to display content if the user has any of the given permissions.

	If no array props are given, you can specify a `domain` and `action` strings directly, as a shorthand
	to check for a specific permission.

	Example Usage:

		let addCreativeAllowablePermissions = [
			{
				domain: "Creative View",
				action: "Upload Ad Files",
			},
			{
				domain: "Creative View",
				action: "Upload VAST",
			},
			{
				domain: "Creative View",
				action: "Upload Assets",
			}
		];

		<Permissioned
			any = {addCreativeAllowablePermissions}
		>
			<AddCreativeButton>
				Only show me if the user has permission to upload some kind of creative in the `Creative View`
			</AddCreativeButton>
		</Permissioned>

		<Permissioned
			domain = "Creative Library"
			action = "Create"
		>
			<AddLibraryButton>
				Only show me if the user has permission to `Create` a `Creative Library`
			</AddLibraryButton>
		</Permissioned>


	Note:
		This has a very basic implementation that may cause performance issues down the line.

		e.g. If there are many of these on grid items.

		If that happens, basic memoization should resolve it, but the action sets are pretty
		small per domain, and domains are currently looked up in constant time.
*/

// import React from "react"; // Not actually using any JSX in here, but if you add any, you need this import.
import { useSelector } from "react-redux";
import { hasPermission } from "utils/common";

export default function Permissioned({
	any = [], // Array of permissions, where if the user has any of them, we display the children

	/* If array options aren't specified, we provide a shorthand for checking a specific permission */
	domain,
	action,

	children,
}){
	const user = useSelector(state => state.you);
	let shouldShowContent = false;

	if(any.length){
		any.forEach(({ domain, action }) => {
			if(hasPermission(user, domain, action)){
				shouldShowContent = true;
				return;
			}
		});
	} else if(domain && action){
		shouldShowContent = hasPermission(user, domain, action);
	}

	if(shouldShowContent){
		return children;
	}
	return null;
}
